import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import { initFirebase } from "@/lib/firebase";
import { UserProvider } from "@/context/UserContext";

// Suppress expected Firebase permission-denied errors during logout
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  // Convert all arguments to strings for pattern matching
  const fullMessage = args.map(arg => String(arg)).join(' ');
  
  // Filter out Firestore permission-denied errors (expected during logout)
  if (
    fullMessage.includes('Firestore') && 
    fullMessage.includes('permission-denied') &&
    fullMessage.includes('Missing or insufficient permissions')
  ) {
    // These are expected when users log out - listeners clean up gracefully
    return;
  }
  
  // Filter out browser extension message channel errors (caused by extensions, not our code)
  if (fullMessage.includes('message channel closed') || 
      fullMessage.includes('listener indicated an asynchronous response')) {
    // This is caused by browser extensions interfering with the page
    return;
  }

  // Firestore multi-tab IndexedDB lease contention — harmless SDK coordination noise
  // that occurs when multiple tabs are open. Does not affect functionality.
  if (fullMessage.includes('Failed to obtain primary lease')) {
    return;
  }

  // Firestore channel 400 on TYPE=terminate — the server already closed the connection
  // before the client sent its cleanup request. Standard race on tab close / navigation.
  if (
    (fullMessage.includes('Firestore/Listen/chann') || fullMessage.includes('Firestore/Write/chann')) &&
    fullMessage.includes('TYPE=terminate') &&
    fullMessage.includes('400')
  ) {
    return;
  }
  
  originalConsoleError.apply(console, args);
};

(async () => {
  try {
    await initFirebase(); // <- ensures auth/db/storage are ready
  } catch (e) {
    originalConsoleError("Firebase init failed", e);
    // Optional: render a fatal error screen here
  }

  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <BrowserRouter>
        <UserProvider>
          <App />
        </UserProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
})();
